import { useEffect, useState } from 'react'
import { Flex } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from '@tanstack/react-router'
import { useGetChapter } from '@/services/chapterService'
import { moduleService } from '@/services/modulesService'
import { useGetQuesitons } from '@/services/questionsService'
import { QuestionsItemType } from '@/types/module'
import { useModulesStore } from '@/stores/useModulesStore'
import { useDebounce } from '@/hooks/useDebounce'
import SearchBox from '@/components/ui/SearchBox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import TablePaginate from '@/components/ui/table-paginate'
import BreadScrum from '@/components/BreadScrum'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import ChoicesQuestion from './components/ChoicesQuestion'
import ParagraphQuestion from './components/ParagraphQuestion'
import QuestionDrawer from './components/QuestionDrawer'
import { Types, TypeValue } from './constant'
import { getQuestionType, getSelectionChapter, getSelectionType } from './utils'

const QuestionCollection = () => {
  const { quesiontionLibId } = useParams({
    from: '/_authenticated/question-lib/$quesiontionLibId',
  })
  const searchParams = useSearch({
    from: '/_authenticated/question-lib/$quesiontionLibId',
  }) as { source?: string }
  const isFromChapterTable = searchParams?.source === 'chapterTable'

  const { selectedModule, selectedChapter, setSelectedQuestion } =
    useModulesStore()

  const { chapters } = useGetChapter({
    page: 1,
    limit: 100,
    module_id: String(selectedModule?.id ?? ''),
    search: '',
  })

  const { data: sections } = useQuery({
    queryKey: ['sections', selectedModule?.id],
    queryFn: () =>
      moduleService.getSectionsByModuleId(Number(selectedModule?.id)),
  })

  const [quesntionId, setQuesionId] = useState<number>(0)
  const [questionType, setQuestionType] = useState('')

  const [type, setType] = useState('all')
  const [chapter, setChapter] = useState(
    quesiontionLibId === '0' ? 'all' : selectedChapter?.chapter_id.toString()
  )
  const [section, setSection] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const debouncedQuery = useDebounce(search)

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery])

  const params = {
    module_id: selectedModule?.id as number,
    chapter_id: chapter || '',
    query: debouncedQuery,
    type: type,
    page,
    section,
  }

  const { questions, isLoadingQuestions, meta, refetchQuestions } =
    useGetQuesitons(params)

  return (
    <Main>
      <BreadScrum
        breadcrumbs={[
          {
            link: isFromChapterTable ? '/class' : '/question-lib/',
            label: isFromChapterTable ? 'Class' : 'Question Library',
          },
          { link: '', label: selectedModule?.name ?? '' },
          {
            link: '',
            label:
              quesiontionLibId === '0' && chapter === 'all'
                ? 'All'
                : (getSelectionChapter(
                    (chapter as string).toString(),
                    chapters
                  ) ?? ''),
          },
        ]}
      />
      <PageTitle title={`Question list (${meta?.total_items ?? 0})`} />
      <Flex justify={'between'}>
        <SearchBox onValueChange={setSearch} />
        <Flex align={'center'} gap={'4'}>
          {/* section  */}
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className='w-fit md:w-[200px]'>
              <SelectValue className='text-sm'>
                <span className='text-[#101828]'>Section:</span>
                <span className='font-light text-[#475467]'>
                  {section === 'all' ? 'All' : <>{section}</>}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                onClick={() => {
                  setSection('all')
                  setPage(1)
                }}
                value={'all'}
              >
                All
              </SelectItem>
              {sections?.sections?.map((item) => (
                <SelectItem
                  onClick={() => {
                    setSection(item)
                    setPage(1)
                  }}
                  key={item}
                  value={item}
                >
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* chapter */}
          <Select value={chapter} onValueChange={setChapter}>
            <SelectTrigger className='w-fit md:w-[200px]'>
              <SelectValue className='text-sm'>
                <span className='text-[#101828]'>Chapter:</span>
                <span className='font-light text-[#475467]'>
                  {chapter === 'all' ? (
                    'All'
                  ) : (
                    <>
                      {getSelectionChapter(
                        (chapter as string).toString(),
                        chapters
                      )}
                    </>
                  )}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                onClick={() => {
                  setChapter('all')
                  setPage(1)
                }}
                value={'all'}
              >
                All
              </SelectItem>
              {chapters?.map((item) => (
                <SelectItem
                  onClick={() => {
                    setChapter(item.chapter_id.toString())
                    setPage(1)
                  }}
                  key={item.chapter_id}
                  value={item.chapter_id.toString()}
                >
                  {item.chapter_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* type  */}
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className='w-fit md:w-[200px]'>
              <SelectValue className='text-sm'>
                <span className='text-[#101828]'>Type:</span>
                <span className='font-light text-[#475467]'>
                  {getSelectionType(type)}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {Types.map((item) => (
                <SelectItem
                  onClick={() => {
                    setType(item.value)
                    setPage(1)
                  }}
                  value={item.value}
                >
                  {item.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Flex>
      </Flex>
      <Flex direction={'column'} gap={'12px'} mt={'4'}>
        {isLoadingQuestions ? (
          <>
            {[...Array(3)].map(() => (
              <Skeleton className='h-30 w-full bg-[#d6d6d6]' />
            ))}
          </>
        ) : (
          <>
            {questions?.length === 0 && (
              <Flex justify={'center'} className='h-24 text-center'>
                No data found.
              </Flex>
            )}
            {questions?.map((item: QuestionsItemType) => {
              const questionType = !item.case_study_id
                ? item.sub_questions[0].type
                : TypeValue.case_study

              if (
                questionType === TypeValue.case_study ||
                questionType === TypeValue.text
              ) {
                return (
                  <ParagraphQuestion
                    questionInfo={item.question_info}
                    section={item.section}
                    key={item.question_id}
                    cantEdit={!isFromChapterTable}
                    in_pt={item.in_pt}
                    mark={item.sub_questions.reduce(
                      (sum, sq) => sum + sq.marks,
                      0
                    )}
                    time={item.sub_questions.reduce(
                      (sum, sq) => sum + sq.time_limit,
                      0
                    )}
                    question_id={item.question_id}
                    question={item.text}
                    questionType={getQuestionType(questionType)}
                    createdAt={item.created_at}
                    onDeleteSuccess={() => {
                      refetchQuestions()
                    }}
                    onClick={() => {
                      setSelectedQuestion(item)
                      // navigate({
                      //   to: '/question-lib/$questionLibId/$questionId',
                      //   params: {
                      //     questionLibId: quesiontionLibId.toString(),
                      //     questionId: String(item.question_id),
                      //   },
                      //   search: { source: searchParams?.source },
                      // })
                      setQuesionId(item.question_id)
                      setQuestionType(questionType)
                    }}
                  />
                )
              }

              return (
                <ChoicesQuestion
                  key={item.question_id}
                  questionData={item}
                  refreshQuestionList={() => refetchQuestions()}
                />
              )
            })}
          </>
        )}
      </Flex>
      {meta && meta?.total_pages > 1 && (
        <div className='flex justify-end p-4'>
          <TablePaginate
            itemPerPage={meta.items_per_page}
            totalItems={meta.total_items}
            page={page}
            totalPages={meta.total_pages}
            onPageChange={setPage}
            readOnlyPageDisplay={false}
            hideOnSinglePage={false}
          />
        </div>
      )}

      {!!quesntionId && (
        <QuestionDrawer
          typeValue={questionType}
          fromChapterTable={isFromChapterTable}
          questionId={quesntionId}
          setIsOpen={setQuesionId}
          onDeleteSuccess={() => {
            void refetchQuestions()
          }}
        />
      )}
    </Main>
  )
}

export default QuestionCollection
